import React, { ReactElement, useEffect, useMemo, useState } from "react";

import { Button, Dimmer, Icon, Image, Loader, Modal } from "semantic-ui-react";

import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";

import ErrorMessage from "react-lib/apps/common/ErrorMessage";
import ModInfo from "react-lib/apps/common/ModInfo";

import CheckInformation from "./CheckInformation";
import { InformationDataType } from "./InformationFormBasic";
import { InformationDetailedDataType } from "./InformationFormDetailed";
import RegisterComplete from "./RegisterComplete";
import RegisterInformation from "./RegisterInformation";

import config from "config/config";

type CreateOAProps = {
  onEvent: (e: any) => any;
  controller: any;
  apiToken: string;
  layout?: "basic" | "detailed";
  myDivisions: any[];
  userFullName?: string;
  userRoleList?: {
    id: number | string;
    name: string;
  }[];
  masterData: any;
  masterOptions: any;
  onCreated: (id: any) => Promise<void>;
  onGetDivision: (type: string) => void;
  onSetShowMenuNoti?: (show: boolean) => void;
  onSuccess?: () => void;
  languageUX: "en" | "th";
};

type Step = {
  content: () => ReactElement;
  label: string;
  name: string;
};

type StepProgressBarProps = {
  style?: React.CSSProperties;
  activeStep: number;
  steps: Step[];
};

type UserProfile = InformationDataType &
  InformationDetailedDataType & {
    first_name: string;
    last_name: string;
  };

type DoctorOption = {
  key: number;
  text: string;
  value: number | string;
};

type SuccessData = {
  account_link: string;
  account_name: string;
};

type ErrorState = {
  error: any;
  open: boolean;
};

type PostRequestOAParams = {
  address?: string;
  apiToken: string;
  branch_name?: number;
  branch_type?: string;
  business_name?: string;
  business_type?: string;
  detail: string;
  first_name?: string;
  hospitalName?: string;
  last_name?: string;
  name: string;
  tax_payer_id?: string;
  accounting_address_text?: string;
  accounting_business_name?: string;
  accounting_city?: string;
  accounting_district?: string;
  accounting_province?: string;
  accounting_tax_payer_id?: string;
  accounting_image_file?: File | string;
};

const IMAGES = {
  newCredit: "/static/images/mh-new-credit-cropped.png",
};

const StepProgressBar: React.FC<StepProgressBarProps> = (props) => {
  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    let index: number;

    if (props.activeStep < 0) {
      index = 0;
    } else if (props.activeStep >= props.steps.length) {
      index = props.steps.length - 1;
    } else {
      index = props.activeStep;
    }

    setActiveStep(index);
  }, [props.activeStep, props.steps.length]);

  const getStepClassName = (index: number): string => {
    if (index < activeStep) {
      return "step is-complete";
    }

    if (index === activeStep) {
      return "step is-active";
    }

    return "step";
  };

  return (
    <>
      <ol className="steps oa" style={{ margin: "auto", ...props.style }}>
        {props.steps.map((step, index) => (
          <li key={`step-${index}`} className={getStepClassName(index)} data-step={index + 1}>
            {step.label}
          </li>
        ))}
      </ol>
      {props.steps[activeStep]?.content?.()}
    </>
  );
};

const CreateOA: React.FC<CreateOAProps> = (props) => {
  const history = useHistory();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [showCreateScreen, setShowCreateScreen] = useState<string>("");
  const [data, setData] = useState<UserProfile>({
    first_name: "",
    last_name: "",
  });
  const [successData, setSuccessData] = useState<SuccessData>({
    account_link: "",
    account_name: "",
  });
  const [showNoti, setShowNoti] = useState<boolean>(false);
  const [openModError, setOpenModError] = useState<ErrorState>({
    error: null,
    open: false,
  });

  const [healthFacilityTypeOptions, setHealthFacilityTypeOptions] = useState<Record<string, any>[]>(
    [],
  );

  const [openBonusNewMod, setOpenBonusNewMod] = useState<boolean>(false);
  const [bonusData, setBonusData] = useState<any>(null);

  useEffect(() => {
    props.onGetDivision("create_oa");
    props.onSetShowMenuNoti?.(true);

    props.onEvent({
      message: "GetMasterData",
      params: {
        masters: [
          ["province", {}],
          ["district", {}],
          ["city", {}],
        ],
      },
    });
  }, []);

  useEffect(() => {
    if (props.myDivisions.length >= 2 || props.layout === "detailed") {
      setShowCreateScreen("open");

      props.onSetShowMenuNoti?.(false);
    } else {
      setShowCreateScreen("close");
    }
  }, [props.myDivisions]);

  useEffect(() => {
    const getProfile = async ({ apiToken }: { apiToken: string }): Promise<void> => {
      const [profile] = await props.controller.getUserProfile({
        apiToken,
      });

      let username = "";

      username = props.userFullName || `${profile?.first_name} ${profile?.last_name}`;

      if (profile) {
        setData({ ...profile, image: "", username });
      }
    };

    const apiToken = Cookies.get("apiToken") || "";

    if (apiToken && props.controller?.getUserProfile) {
      void getProfile({ apiToken });
    }
  }, [props.controller?.getUserProfile, props.userFullName]);

  useEffect(() => {
    const getHealthFacilityType = async (apiToken: string) => {
      if (!props.controller.getHealthFacilityType) {
        return;
      }

      const [types]: [Record<string, any>[]] = await props.controller.getHealthFacilityType({
        apiToken,
      });

      const options = types.map((item: any) => ({
        key: item.name,
        text: item.name,
        value: item.name,
      }));

      setHealthFacilityTypeOptions(options);
    };

    const apiToken = Cookies.get("apiToken") || "";

    if (apiToken) {
      void getHealthFacilityType(apiToken);
    }
  }, []);

  const handleSaveRegister = (saveData: Partial<UserProfile>): void => {
    setData({
      ...data,
      ...saveData,
    });
    setActiveStep(1);
  };

  const handleSaveCheck = async (): Promise<void> => {
    const {
      accountName,
      address,
      business,
      description,
      accounting,
      division_address: divisionAddress,
      has_staff: hasStaff,
      hospital_name: hospitalName,
      oa_type: oaType,
    } = data;
    const params: PostRequestOAParams = {
      apiToken: props.apiToken,
      branch_name: business?.branch_name ?? undefined,
      branch_type: business?.branch_type || "",
      business_name: business?.business_name || "",
      business_type: business?.business_type || "",
      detail: description || "",
      first_name: business?.first_name || "",
      last_name: business?.last_name || "",
      name: accountName || "",
      tax_payer_id: business?.tax_payer_id || "",
    };

    const [res, error] = await props.controller.prxManager.postRequestOA(params);

    if (error) {
      setOpenModError({
        error,
        open: true,
      });

      return;
    }

    if (!res) {
      return;
    }

    const [divProfile] = await props.controller.prxManager.getOADivisionProfile({
      apiToken: props.apiToken,
      divisionId: res.id,
    });

    setSuccessData({
      account_link: `${config.OFFICIAL_ACCOUNT_HOST || "https://ishealth.app"}/OA/${
        divProfile.id
      }/?openExternalBrowser=1`,
      account_name: res.name,
    });

    await props.onCreated(res.id);

    await props.controller.prxManager.patchOADivisionProfile({
      id: res.id,
      address,
      addressText: divisionAddress?.address_text,
      apiToken: props.apiToken,
      divisionId: res.id,
      hasStaff,
      hospitalName,
      oaType,
      placeId: divisionAddress?.place_id,
      ...divisionAddress,
      business,
      accountingAddressText: accounting?.accounting_address_text,
      accountingBusinessName: accounting?.accounting_business_name,
      accountingCity: accounting?.accounting_city,
      accountingDistrict: accounting?.accounting_district,
      accountingProvince: accounting?.accounting_province,
      accountingTaxPayerId: accounting?.accounting_tax_payer_id,
      accountingImageFile: accounting?.accounting_image_file,
    });

    // -- Bonus Credit Price
    const bonusResp = await props.onEvent({
      message: "HandleGetBonusCreditForNewOA",
      params: {},
    });

    setBonusData(bonusResp);

    handleOpenBonusNewMod();
    // setActiveStep(2);
  };

  const handleCancelCheck = (): void => {
    setActiveStep(0);
  };

  const handleSuccessRegister = (): void => {
    // globalThis.location.replace("/Chat");
    if (props.layout === "detailed") {
      props.onSuccess?.();
    }
  };

  const handleCreateScreenBack = (): void => {
    setData({
      first_name: "",
      last_name: "",
    });

    if (props.myDivisions.length >= 2) {
      history.goBack();
    } else {
      setShowCreateScreen("close");
      props.onSetShowMenuNoti?.(true);
    }
  };

  const handleCloseModInfo = (): void => {
    setOpenModError({
      error: null,
      open: false,
    });
  };

  const handleOpenBonusNewMod = () => {
    setOpenBonusNewMod(true);
  };

  const handleCloseBonusNewMod = () => {
    setOpenBonusNewMod(false);
    setBonusData(null);

    setActiveStep(2);
  };

  const doctorOptions: DoctorOption[] = useMemo(
    () =>
      (props.userRoleList ?? []).map((list, index) => ({
        key: index,
        text: list.name,
        value: list.id,
      })),
    [props.userRoleList],
  );

  const steps: Step[] = useMemo(
    () => [
      {
        label: "ลงทะเบียนข้อมูล Official Account",
        name: "step 1",
        content: () => (
          <div style={{ marginTop: "50px" }}>
            <RegisterInformation
              data={data}
              layout={props.layout}
              masterData={props.masterData}
              masterOptions={props.masterOptions}
              healthFacilityTypeOptions={healthFacilityTypeOptions}
              onCancel={handleCreateScreenBack}
              onSave={handleSaveRegister}
              languageUX={props.languageUX}
            />
          </div>
        ),
      },
      {
        label: "ตรวจสอบข้อมูลการสมัคร",
        name: "step 2",
        content: () => (
          <div style={{ marginTop: "50px" }}>
            <CheckInformation
              data={data}
              layout={props.layout}
              masterData={props.masterData}
              masterOptions={props.masterOptions}
              healthFacilityTypeOptions={healthFacilityTypeOptions}
              onCancel={handleCancelCheck}
              onSave={handleSaveCheck}
            />
          </div>
        ),
      },
      {
        label: "เสร็จสิ้นการสมัคร",
        name: "step 3",
        content: () => (
          <div style={{ marginTop: "65px" }}>
            <RegisterComplete
              data={successData}
              layout={props.layout}
              onSuccess={handleSuccessRegister}
            />
          </div>
        ),
      },
    ],
    [
      data,
      doctorOptions,
      handleCancelCheck,
      handleCreateScreenBack,
      handleSaveCheck,
      handleSaveRegister,
      handleSuccessRegister,
      healthFacilityTypeOptions,
      props.layout,
      successData,
    ],
  );

  return (
    <div className="oa-form-custom">
      {showCreateScreen === "close" ? (
        // หน้าแรกก่อนเข้าหน้า register
        <>
          {/* Notification */}
          {showNoti && (
            <div className="card-noti-oa">
              <div>
                <div
                  style={{
                    alignItems: "baseline",
                    display: "flex",
                    marginBottom: "10px",
                  }}
                >
                  <Icon color="blue" name="hospital" />
                  <div className="title">แผนกอายุรกรรม</div>
                </div>
                <div>
                  คุณร้องขอเข้าใช้งาน Official Account &quot;แผนกอายุรกรรม&quot;
                  แล้วกรุณารอการตรวจสอบจากแอดมิน
                </div>

                <div className="btn-remove">
                  <div
                    style={{
                      cursor: "pointer",
                      padding: "8px",
                      width: "120px",
                    }}
                    onClick={() => setShowNoti(false)}
                  >
                    ลบการแจ้งเตือน
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              height: "94vh",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                color: "rgb(63 60 60)",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              คุณไม่มี Official Account ที่สามารถใช้งานได้
            </div>
            <div
              style={{
                color: "rgb(63 60 60)",
                fontSize: "18px",
                fontWeight: "bold",
                margin: "12px 0 40px",
              }}
            >
              กรุณา &quot;สร้าง Official Account&quot; เพื่อเข้าใช้งาน
            </div>
            <div>
              <Button
                className="second"
                color="blue"
                size="huge"
                onClick={() => {
                  setShowCreateScreen("open");
                  props.onSetShowMenuNoti?.(false);
                }}
              >
                สร้าง Official Account
              </Button>
            </div>
          </div>
        </>
      ) : (
        showCreateScreen === "open" && (
          <>
            {/* Popup Credit */}
            <Modal open={openBonusNewMod} size="small" onClose={handleCloseBonusNewMod}>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  justifyContent: "center",
                  padding: "1rem",
                  width: "100%",
                }}
              >
                <Image style={{ display: "block", width: "70%" }} src={IMAGES.newCredit} />
                <label style={{ fontSize: "1.5rem" }}>
                  โบนัสสำหรับ Official Account ใหม่{" "}
                  <span style={{ color: "#FB5380" }}>
                    {Number(bonusData?.price || "0.00").toLocaleString("th-TH", {
                      minimumFractionDigits: 2,
                    })}
                  </span>{" "}
                  Credits
                </label>
                <Button
                  style={{
                    backgroundColor: "var(--primary-theme-color)",
                    color: "white",
                    width: "10rem",
                  }}
                  onClick={handleCloseBonusNewMod}
                >
                  กลับสู่เมนูหลัก
                </Button>
              </div>
            </Modal>

            {/* หน้า Register */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "100%",
                height: "94vh",
                overflow: "auto",
              }}
            >
              <div style={{ padding: "20px" }}>
                <ModInfo
                  key="modError"
                  open={openModError.open}
                  titleColor="red"
                  onApprove={handleCloseModInfo}
                  onClose={handleCloseModInfo}
                >
                  <ErrorMessage error={openModError.error} />
                </ModInfo>
                <div>
                  <StepProgressBar style={{ width: "80%" }} activeStep={activeStep} steps={steps} />
                </div>
              </div>
            </div>
          </>
        )
      )}

      {!["close", "open"].includes(showCreateScreen) && (
        <Dimmer active inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>
      )}
    </div>
  );
};

export default CreateOA;
